import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, Input, InputNumber, Modal, Select, message } from "antd";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { T } from "../../../shared/ui/PageShell";
import {
  fetchExpenseTypes,
  expenseTypeLabelMap,
} from "../lib/expenseTypes";

/**
 * Shared "Add expense" modal for farmer charges.
 * Used from Charges tab and Reports tab (Farmer Finance).
 */
export default function AddVendorExpenseModal({
  open,
  onClose,
  onSuccess,
  /** Prefill farmer when opened from Reports with a selection */
  initialVendorId = null,
  vendors: vendorsProp = null,
}) {
  const { t, i18n } = useTranslation();
  const mainCode = readCachedSystemConfig()?.currency_code || "IQD";

  const [vendors, setVendors] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [addVendor, setAddVendor] = useState(null);
  const [addProduct, setAddProduct] = useState(null);
  const [addProducts, setAddProducts] = useState([]);
  const [addAmounts, setAddAmounts] = useState({});
  const [addNote, setAddNote] = useState("");
  const [addExpenseAt, setAddExpenseAt] = useState(() => dayjs());
  const [saving, setSaving] = useState(false);

  const expLabels = useMemo(() => expenseTypeLabelMap(expenseTypes), [expenseTypes]);
  const expenseCategoryCodes = useMemo(
    () => (expenseTypes.length ? expenseTypes.map((x) => x.code) : ["rent", "transport", "moving", "watching", "other"]),
    [expenseTypes]
  );
  const addModalTotal = Object.values(addAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const vendorsWithBalance = useMemo(
    () => vendors.filter((vendor) => toApiNumber(vendor.consignment_owed, 0) > 0.01),
    [vendors]
  );

  const refreshVendors = useCallback(async () => {
    try {
      const response = await apiClient.get("/vendors");
      const list = Array.isArray(response.data) ? response.data : [];
      setVendors(list);
      return list;
    } catch {
      const fallback = Array.isArray(vendorsProp) ? vendorsProp : [];
      setVendors(fallback);
      return fallback;
    }
  }, [vendorsProp]);

  const selectAddVendor = async (vendorId) => {
    setAddVendor(vendorId);
    setAddProduct(null);
    setAddProducts([]);
    if (!vendorId) return;
    try {
      const response = await apiClient.get(`/pos/vendor-consignment/${vendorId}`);
      setAddProducts(Array.isArray(response.data?.products) ? response.data.products : []);
    } catch {
      setAddProducts([]);
    }
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    (async () => {
      setAddProduct(null);
      setAddProducts([]);
      setAddAmounts({});
      setAddNote("");
      setAddExpenseAt(dayjs());

      const [list, types] = await Promise.all([
        refreshVendors(),
        fetchExpenseTypes({ force: true }),
      ]);
      if (cancelled) return;

      setExpenseTypes((types || []).filter((r) => r.is_active !== false));

      const preferredId = initialVendorId != null ? Number(initialVendorId) : null;
      const withBalance = (list || []).filter((v) => toApiNumber(v.consignment_owed, 0) > 0.01);
      const canPrefill =
        preferredId
        && withBalance.some((v) => Number(v.id) === preferredId);

      if (canPrefill) {
        await selectAddVendor(preferredId);
      } else {
        setAddVendor(null);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal opens
  }, [open, initialVendorId, refreshVendors]);

  const submitAdd = async () => {
    if (!addVendor) {
      message.error(t("vendorSelectFarmer", "Select a farmer"));
      return;
    }
    const expenses = Object.entries(addAmounts)
      .map(([type, amount]) => ({ type, amount: parseFloat(amount) || 0 }))
      .filter((row) => row.amount > 0);
    if (!expenses.length) {
      message.error(t("vendorsPaymentInvalid", "Enter a valid amount"));
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/vendors/${addVendor}/expenses`, {
        expenses,
        product_id: addProduct || null,
        note: addNote || null,
        expense_at: addExpenseAt ? addExpenseAt.format("YYYY-MM-DD HH:mm:ss") : null,
      });
      message.success(t("vendorExpenseAdded", "Expense added"));
      onClose?.();
      onSuccess?.(addVendor);
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorExpenseFailed", "Could not add expense"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("vendorAddExpense", "Add expense")}
      open={open}
      onOk={submitAdd}
      confirmLoading={saving}
      onCancel={onClose}
      okText={t("purchaseOrdersSave", "Save")}
      cancelText={t("cancel", "Cancel")}
      centered
      destroyOnHidden
      width={440}
    >
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{t("vendorsColName", "Farmer")}</div>
      <Select
        showSearch
        optionFilterProp="label"
        style={{ width: "100%", marginBottom: 12 }}
        placeholder={t("vendorSelectFarmerWithBalance", "Select a farmer with balance to clear")}
        value={addVendor}
        onChange={selectAddVendor}
        notFoundContent={t("noFarmersWithBalance", "No farmers with money to clear")}
        options={vendorsWithBalance.map((v) => ({
          value: v.id,
          label: [
            v.name,
            v.consignment_owed != null
              ? `(${formatCurrency(v.consignment_owed, mainCode)})`
              : null,
          ].filter(Boolean).join(" — "),
        }))}
      />
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>
        {t("chargeScope", "Charge applies to")}
      </div>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: "100%", marginBottom: 12 }}
        placeholder={t("allProductsFullAccount", "All products / full farmer account")}
        value={addProduct}
        onChange={(value) => setAddProduct(value ?? null)}
        options={addProducts.map((product) => ({
          value: product.product_id,
          label: parseLocalizedName(product.name, i18n.language, `#${product.product_id}`),
        }))}
      />
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
        {addProduct
          ? t("vendorExpenseProductHint", "This charge will be deducted only from the selected product.")
          : t("vendorExpenseFullHint", "This charge applies to the farmer's full account. Fill any types; leave blank to skip.")}
      </div>
      {expenseCategoryCodes.map((type) => (
        <div key={type} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1, fontWeight: 600, color: T.text }}>{expLabels[type] || type}</div>
          <InputNumber
            min={0}
            style={{ width: 160 }}
            value={addAmounts[type] ?? null}
            placeholder="0"
            onChange={(v) => setAddAmounts((prev) => ({ ...prev, [type]: v ?? 0 }))}
          />
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 14px", paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        <span style={{ color: T.muted, fontSize: 12 }}>{t("total", "Total")}</span>
        <b style={{ color: "#d97706" }}>{formatCurrency(addModalTotal, mainCode)}</b>
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{t("dateTime", "Date & time")}</div>
      <DatePicker
        showTime={{ format: "HH:mm" }}
        format="DD/MM/YYYY HH:mm"
        value={addExpenseAt}
        onChange={(v) => setAddExpenseAt(v || dayjs())}
        allowClear={false}
        needConfirm={false}
        style={{ width: "100%", marginBottom: 12 }}
      />
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{t("note", "Note")}</div>
      <Input value={addNote} onChange={(e) => setAddNote(e.target.value)} placeholder={t("note", "Note")} />
    </Modal>
  );
}
